import { TOptions } from "i18next";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@/atoms-react/button/Button.react";
import Image from "@/atoms-react/image/Image.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { LE_PAYMENT_DATA_NOT_FOUND_INTERNAL_CODE } from "@/features/company/types";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { Themes } from "@/types/theme";
import { PaymentRadio } from "./atoms/PaymentRadio";
import { CompanyPaymentBankTransfer } from "./components/company-payment-bank-transfer";
import { CompanyPaymentCard } from "./components/company-payment-card";

enum PaymentMethods {
  CARD = "CARD",
  BANK_TRANSFER = "BANK_TRANSFER"
}

type CompanyPaymentProps = {
  canInitPayment: boolean;
};

export const CompanyPayment = ({ canInitPayment }: CompanyPaymentProps) => {
  const { getLanguage } = usei18n();
  const { t } = useTranslation(["pass", "error"]);
  const tr = (key: string, options?: TOptions) => t(`companyPayment.${key}`, options);
  const { leUuid } = useCompanyContext();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethods>(PaymentMethods.BANK_TRANSFER);
  const [isError, setIsError] = useState<boolean>(false);
  const [cardPaymentFinished, setCardPaymentFinished] = useState<boolean>(false);

  const { companyPaymentInformationsQuery, initCompanyPaymentMutation, companyDetailQuery } =
    useCompany();
  const { refetch: refetchCompanyDetail } = companyDetailQuery(leUuid);

  const {
    data: paymentData,
    error: paymentDataError,
    refetch: refetchPaymentData
  } = companyPaymentInformationsQuery(!!leUuid && canInitPayment);
  const { mutateAsync: initCompanyPayment, isError: isInitCompanyPaymentError } =
    initCompanyPaymentMutation();

  const pollingIntervalIdRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (isInitCompanyPaymentError) {
      setIsError(true);
    }
  }, [isInitCompanyPaymentError]);

  useEffect(() => {
    if (cardPaymentFinished) {
      pollPaymentData();
    }
  }, [cardPaymentFinished]);

  useEffect(() => {
    if (!paymentDataError || !leUuid) return;

    if (paymentDataError.internalCode === LE_PAYMENT_DATA_NOT_FOUND_INTERNAL_CODE) {
      initCompanyPayment(leUuid);
    } else {
      setIsError(true);
    }
  }, [paymentDataError, leUuid]);

  const clearPollingInterval = () => {
    if (!pollingIntervalIdRef.current) return;

    clearInterval(pollingIntervalIdRef.current);
  };

  const pollPaymentData = async () => {
    // Max 2 minutes
    let attempts = 0;
    const maxAttempts = 80;

    pollingIntervalIdRef.current = setInterval(async () => {
      try {
        const { data } = await refetchPaymentData();

        if (data && data?.dueAmount <= 0) {
          clearPollingInterval();
          await refetchCompanyDetail();
          toast.success(tr("payment_card.successMessage"));
        }
      } catch (_) {
      } finally {
        attempts++;
        if (attempts >= maxAttempts) {
          clearPollingInterval();
          toast.error(t("bitbang.internal_code.default"));
          setIsError(true);
        }
      }
    }, 1500);
  };

  const formatPrice = (amount: number) => {
    const language = getLanguage() === "fr" ? "fr-FR" : "en-US";

    return new Intl.NumberFormat(language, {
      style: "currency",
      currency: "EUR"
    }).format(amount / 100);
  };

  const reloadPage = () => window.location.reload();

  if (isError) {
    return (
      <div className="mt-5 flex flex-col items-center justify-center gap-5 p-5">
        <div className="font-bold font-lg text-black dark:text-white">{tr("error.title")}</div>
        <Button theme={Themes.danger} className="w-auto" onClick={reloadPage}>
          {tr("error.button")}
        </Button>
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div className="mt-10 flex w-full justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="mt-5">
      <div className="px-4">
        {paymentData?.paymentRecords?.length ? (
          <>
            <p className="text-slate-900 dark:text-white">
              {paymentData.paymentRecords.length === 1
                ? tr("price_display_history.history_title_single_transaction")
                : tr("price_display_history.history_title")}
            </p>

            <ul className="my-5 list-disc space-y-2 pl-8">
              {paymentData.paymentRecords.map((p, i) => (
                <li
                  key={`payment-record-${p.date}-${p.amount}`}
                  className="text-slate-900 dark:text-white"
                >
                  <Trans
                    i18nKey="companyPayment.price_display_history.transaction"
                    values={{
                      number: i + 1,
                      price: formatPrice(p.amount)
                    }}
                    components={{
                      price: <span className="font-bold text-[#01B57C]" />
                    }}
                  />
                </li>
              ))}
            </ul>

            <p className="text-slate-900 dark:text-white">
              <Trans
                i18nKey="companyPayment.price_display_history.history_summary"
                values={{
                  amountPaid: formatPrice(paymentData.totalAmount - paymentData.dueAmount),
                  totalAmount: formatPrice(paymentData.totalAmount)
                }}
                components={{
                  amountpaid: <span className="font-bold text-[#01B57C]" />,
                  totalamount: <span className="font-bold text-primary" />
                }}
              />
            </p>

            <p className="my-8 text-lg text-slate-900 dark:text-white">
              <Trans
                i18nKey="companyPayment.price_display_history.paiement_due"
                values={{ price: formatPrice(paymentData.dueAmount) }}
                components={{
                  price: <span className="font-bold text-primary" />
                }}
              />
            </p>
          </>
        ) : (
          <>
            <p className="mb-2 text-slate-900 text-sm dark:text-white">
              {tr("price_display.desc1")}
            </p>

            <p className="text-lg text-slate-900 dark:text-white">
              <Trans
                i18nKey="companyPayment.price_display.desc2"
                values={{ price: formatPrice(paymentData.totalAmount) }}
                components={{
                  strong: <span className="font-bold" />,
                  price: <span className="font-bold text-primary" />
                }}
              />
            </p>
          </>
        )}

        <div className="mt-5 rounded-tl-md rounded-br-md border border-primary bg-primary/10 px-5 py-2 dark:bg-primary-700 dark:text-white">
          <div className="flex items-center gap-5">
            <div>
              <img src="/images/olkypass/mascotte.png" width="100%" />
            </div>
            <div>{tr("infoCardContent")}</div>
          </div>
        </div>

        <div className="mt-4 flex w-full xs:flex-row flex-col justify-center gap-3 md:justify-start">
          <PaymentRadio
            isActive={paymentMethod === PaymentMethods.BANK_TRANSFER}
            onClick={() => setPaymentMethod(PaymentMethods.BANK_TRANSFER)}
            label={tr("bank_transfer.label")}
            disabled={cardPaymentFinished}
          >
            <div className={"mt-3 flex flex-col items-center justify-center gap-3"}>
              <Image className={"w-1/2"} src="/images/olkypass/feeds/SEPA.png" alt="SEPA" />
              <Image className={""} src="/images/olkypass/feeds/SEPA_INSTANT_FR.png" alt="SEPA" />
            </div>
          </PaymentRadio>

          <PaymentRadio
            isActive={paymentMethod === PaymentMethods.CARD}
            onClick={() => setPaymentMethod(PaymentMethods.CARD)}
            label={tr("payment_card.label")}
            disabled={cardPaymentFinished}
          >
            <div
              className={
                "mt-5 flex h-full w-full flex-row xs:flex-col items-center justify-center gap-3 sm:flex-row"
              }
            >
              <img src="/images/olkypass/feeds/VISA.png" alt="VISA" />
              <img src="/images/olkypass/feeds/CB.png" alt="CB" />
            </div>
          </PaymentRadio>
        </div>

        {cardPaymentFinished ? (
          <div className="my-5 flex justify-center rounded-tl-md rounded-br-md border border-primary bg-primary/10 px-5 py-8 dark:bg-primary-700">
            <div className="my-10 flex w-full flex-col items-center justify-center gap-10">
              <p className="font-bold text-lg">{tr("payment_processing")}</p>
              <Loader />
            </div>
          </div>
        ) : (
          <>
            {paymentMethod === PaymentMethods.BANK_TRANSFER && (
              <CompanyPaymentBankTransfer
                account={paymentData.account}
                address={paymentData.address}
                iban={paymentData.iban}
                bic={paymentData.bic}
                amount={formatPrice(paymentData.dueAmount)}
              />
            )}

            {paymentMethod === PaymentMethods.CARD && (
              <CompanyPaymentCard onFinish={() => setCardPaymentFinished(true)} />
            )}
          </>
        )}
        <p className="mt-8 text-slate-800 dark:text-white">
          {tr("info1")} <br />
          {tr("info2")}
        </p>
      </div>
    </div>
  );
};
